import { record } from "rrweb";
import type { eventWithTime } from "@rrweb/types";
import pako from "pako";
import { onPageChange } from "./tracking/onPageChange";

const MASK_OPTS = { maskAllInputs: true, maskTextSelector: "*" } as const;
const BATCH_SIZE = 1000; // Max number of events to send in a batch
const BATCH_TIMEOUT = 15000; // Max waiting time before sending the batch
const MAX_BATCH_SIZE_BYTES = 1 * 1024 * 1024; // 1MB max batch size
const MAX_RETRIES = 3; // Maximum number of retry attempts for failed requests
const RETRY_DELAY = 1000; // Delay between retries in milliseconds

interface BatchPayload {
  app_id: string;
  session_id: string | null;
  batch_id: string | null;
  events: eventWithTime[];
}

export class SessionRecorder {
  private events: eventWithTime[] = [];
  private stopFn: (() => void) | undefined;
  private started = false;
  private sessionId: string | null = null;
  private batchId: string | null = null;
  private lastFlush = 0;
  private flushTimeoutId: number | undefined;
  private apiBase: string;
  private appId: string;
  private currentBatchSize = 0;
  private isFlushing = false;

  constructor(apiBase: string, appId: string) {
    this.apiBase = apiBase;
    this.appId = appId;
  }

  start(sessionId: string) {
    if (this.started) return;
    this.started = true;
    this.sessionId = sessionId;
    this.batchId = crypto.randomUUID();
    this.lastFlush = Date.now();

    // Begin rrweb recording
    this.stopFn = record({
      emit: (event: eventWithTime) => {
        this.push(event);
      },
      maskAllInputs: MASK_OPTS.maskAllInputs,
      maskTextSelector: MASK_OPTS.maskTextSelector,
      sampling: { mousemove: true, scroll: 150, input: "last" } as any,
      // Essential for replay: capture viewport, URL, and page load events
      recordCanvas: true,
      recordCrossOriginIframes: true,
      recordAfter: "DOMContentLoaded",
      collectFonts: true,
      // Capture CSS changes and media queries
      inlineStylesheet: true,
      // Record page metadata for proper replay context
      slimDOMOptions: {
        comment: true,
        headFavicon: true,
        headWhitespace: true,
        headMetaDescKeywords: true,
        headMetaSocial: true,
        headMetaRobots: true,
        headMetaHttpEquiv: true,
        headMetaAuthorship: true,
        headMetaVerification: true,
      },
    });

    // Flush when the page is hidden/unloaded
    onPageChange(() => this.flush());
  }

  stop() {
    // Force flush any remaining events before stopping
    this.flush();
    this.stopFn?.();
    this.started = false;
    this.events = [];
    this.lastFlush = 0;
    if (this.flushTimeoutId) {
      clearTimeout(this.flushTimeoutId);
      this.flushTimeoutId = undefined;
    }
  }

  private calculateEventSize(event: eventWithTime): number {
    return new TextEncoder().encode(JSON.stringify(this.createPayload([event]))).length;
  }

  private calculateBatchSize(events: eventWithTime[]): number {
    return events.reduce((sum, event) => sum + this.calculateEventSize(event), 0);
  }

  private createPayload(events: eventWithTime[]): BatchPayload {
    return {
      app_id: this.appId,
      session_id: this.sessionId,
      batch_id: this.batchId,
      events,
    };
  }

  private push(event: eventWithTime) {
    const eventSize = this.calculateEventSize(event);

    // If adding this event would exceed the size limit, flush first
    if (this.currentBatchSize + eventSize > MAX_BATCH_SIZE_BYTES) {
      this.flush();
    }

    this.currentBatchSize += eventSize;
    this.events.push(event);

    // If we have too many events in memory, drop the oldest ones
    if (this.events.length > BATCH_SIZE * 2) {
      const oldestEvents = this.events.splice(0, Math.floor(BATCH_SIZE / 2));
      this.currentBatchSize -= this.calculateBatchSize(oldestEvents);
      console.warn(`Dropping ${oldestEvents.length} oldest events due to memory pressure`);
    }

    this.scheduleFlush();
  }

  private scheduleFlush() {
    const reachedSizeThreshold = this.events.length >= BATCH_SIZE;
    const reachedTimeout = this.lastFlush + BATCH_TIMEOUT < Date.now();
    const reachedSizeLimit = this.currentBatchSize >= MAX_BATCH_SIZE_BYTES;

    if (reachedSizeThreshold || reachedTimeout || reachedSizeLimit) {
      this.flush();
      return;
    }

    if (!this.flushTimeoutId) {
      this.flushTimeoutId = setTimeout(() => this.flush(), BATCH_TIMEOUT) as any;
    }
  }

  private resetBatch() {
    this.events = [];
    this.currentBatchSize = 0;
    this.batchId = crypto.randomUUID();
  }

  private async flush(retryCount = 0) {
    if (this.isFlushing) return;
    this.isFlushing = true;

    if (this.flushTimeoutId) {
      clearTimeout(this.flushTimeoutId);
      this.flushTimeoutId = undefined;
    }

    if (this.events.length === 0) {
      this.isFlushing = false;
      return;
    }

    this.lastFlush = Date.now();
    this.events.sort((a, b) => a.timestamp - b.timestamp);

    const payload = this.createPayload(this.events);
    const json = JSON.stringify(payload);
    const compressed = pako.gzip(json);
    const buffer = compressed.buffer.slice(
      compressed.byteOffset,
      compressed.byteOffset + compressed.byteLength
    ) as ArrayBuffer;
    const blob = new Blob([buffer], { type: "application/gzip" });

    const formData = new FormData();
    formData.append("data", blob, "data.json.gz");

    try {
      const response = await this.sendRequest(formData, retryCount);
      if (response.ok) {
        this.resetBatch();
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Failed to send session recording data:", error);
      if (retryCount < MAX_RETRIES) {
        setTimeout(() => this.flush(retryCount + 1), RETRY_DELAY * (retryCount + 1));
      }
    } finally {
      this.isFlushing = false;
    }
  }

  private sendRequest(formData: FormData, retryCount: number): Promise<Response> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${this.apiBase}/widget/replay`, true);

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(new Response(xhr.response, { status: xhr.status }));
        } else {
          reject(new Error(`HTTP error! status: ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error("Network error occurred"));
      };

      xhr.send(formData);
    });
  }
}
